import { PlusOutlined } from '@ant-design/icons'
import { constants } from '@lmc/common'
import { type Home, type ResidentItem, ResidentStatus } from '@lmc/ts-common'
import { Button } from '@lmc/ui-kit'
import Text from '@lmc/ui-kit/src/general/Typography/Text/Text'
import { useAbilityAccess } from 'components/AccessControl'
import useHomeType from 'hooks/useHomeType'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import _ from 'lodash'
import { type FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import Actions from '../../../actions'
import { gaEvent } from '../../../common/services/analytics'
import { getNominative } from '../../../common/utils'
import { ProfileLink, Spinner } from '../../../components/generic'
import { EmptyAvatars, HomeCard } from './CardComponents'

const MAX_RESIDENTS_DISPLAYED_INIT = 10
const VIEW_BUTTON_TEXT = 'View All'

const getTitle = (preferredPeopleNominative: string) =>
  getNominative(preferredPeopleNominative, 'Residents')

const getAddResidentButtonText = (preferredPeopleNominative: string) => {
  if (
    preferredPeopleNominative !== 'Supported Person' &&
    preferredPeopleNominative !== 'Service Users (SU)'
  ) {
    return `Add a ${getNominative(preferredPeopleNominative, 'Resident')}`
  }
  return 'Add'
}

type Props = { residents?: ResidentItem[] }

export const ResidentsCard: FC<Props> = ({ residents }) => {
  const [maxResidentsDisplayed, setMaxResidentsDisplayed] = useState(
    MAX_RESIDENTS_DISPLAYED_INIT
  )

  const preferredPeopleNominative: string = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative || ''
  )

  const { isPro } = useHomeType()
  const { t } = useTranslation(NAMESPACE.GENERAL)

  const hasRolePermission = useAbilityAccess([
    {
      i: 'create',
      this: 'Resident'
    }
  ])

  const dispatch = useDispatch()

  const openModal = () => {
    const id = 'addResident'
    dispatch(Actions.openModal({ modalId: id }))
    dispatch(Actions.addForm({ formId: id }))
  }

  const triggerGAEvent = ({
    action,
    label
  }: {
    action: string
    label?: string
  }) =>
    gaEvent({
      category: 'home-dashboard',
      action,
      label
    })

  const showMore = () => {
    setMaxResidentsDisplayed(maxResidentsDisplayed + 20)
    triggerGAEvent({ action: 'Click show_more_residents' })
  }

  const renderResidents = (activeResidents: ResidentItem[]) => {
    const hiddenResidents = activeResidents.length > maxResidentsDisplayed

    return (
      <div>
        <div className="lmc-flex-grid" style={{ marginTop: 5 }}>
          {_.take(
            activeResidents,
            hiddenResidents ? maxResidentsDisplayed - 1 : maxResidentsDisplayed
          ).map((row) => (
            <ProfileLink
              onClick={() =>
                triggerGAEvent({ action: 'Click resident_profile_link' })
              }
              list="residents"
              key={row.id}
              name={row.name}
              id={row.id}
              picture={row.picture}
            />
          ))}
          {hiddenResidents ? (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <a className="lmc-more-link" onClick={showMore}>
              More...
            </a>
          ) : null}
        </div>
      </div>
    )
  }

  const isLoading = residents?.length === 0 || undefined

  const renderNoResidents = () => <EmptyAvatars />

  const renderBody = () => {
    if (isLoading) return <Spinner style={{ paddingTop: 50 }} />

    const activeResidents = _.chain(residents)
      .filter({ status: ResidentStatus.active })
      .sortBy('name')
      .value()

    if (activeResidents && activeResidents.length)
      return renderResidents(activeResidents)

    return renderNoResidents()
  }

  const renderFooter = () => {
    const link = `/residents`
    if (isLoading) return null

    const hasResidents = !!residents?.length

    const countableResidents =
      residents?.filter(
        (resident) =>
          resident.status &&
          [
            ResidentStatus.active,
            ResidentStatus.inactive,
            ResidentStatus.hospitalised
          ].includes(resident.status)
      ) || []
    const residentCount = countableResidents.length

    const requiresProUpgrade =
      !isPro && residentCount >= constants.MAX_FREE_RESIDENTS

    return (
      <div style={styles.footer}>
        {hasResidents && (
          <div style={styles.viewAllButton}>
            <Link
              onClick={() =>
                triggerGAEvent({ action: 'Click view all residents' })
              }
              to={link}
            >
              <Button variant="default">{VIEW_BUTTON_TEXT}</Button>
            </Link>
          </div>
        )}
        <div id="intro-js-step-add-residents">
          <Button
            icon={<PlusOutlined />}
            variant="primary"
            onClick={() => {
              triggerGAEvent({ action: 'Click create_resident' })
              openModal()
            }}
            disabled={requiresProUpgrade}
            disabledByPermission={requiresProUpgrade || !hasRolePermission}
            permissionDisabledText={
              requiresProUpgrade ? (
                <TooltipContent>
                  <StyledTooltipText level={1}>
                    {t(generalConstants.PRO_UPGRADE_TOOLTIP, {
                      count: constants.MAX_FREE_RESIDENTS,
                      peopleNominative:
                        preferredPeopleNominative?.toLowerCase() || 'residents'
                    })}
                    <Link to="/pro-advert">
                      {t(generalConstants.PRO_UPGRADE_LINK_TEXT)}
                    </Link>
                  </StyledTooltipText>
                </TooltipContent>
              ) : undefined
            }
            withDisabledTooltipIcon={!requiresProUpgrade}
          >
            {getAddResidentButtonText(preferredPeopleNominative)}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <HomeCard
      title={getTitle(preferredPeopleNominative) || '_'}
      titleLink="/residents/list"
      titleLinkDataCy="btn-residents"
      onTitleClick={() =>
        triggerGAEvent({ action: 'Click resident title link' })
      }
      footer={isLoading ? undefined : renderFooter()}
    >
      <div style={styles.offsetBody}>{renderBody()}</div>
    </HomeCard>
  )
}

const TooltipContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const StyledTooltipText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color11};
`

const styles = {
  footer: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  viewAllButton: {
    marginRight: 8,
    maxWidth: 95
  },
  offsetBody: {
    transform: 'translateX(-5px)'
  }
}

export default ResidentsCard
